import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

const PrivacyPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const sections = [
    {
      title: "Information We Collect",
      content: [
        "Personal Information: Name, email address, phone number, and other contact details you provide when creating an account or contacting us.",
        "Educational Data: USMLE scores, medical school information, research experience, clinical rotations, and other academic credentials.",
        "Usage Data: Information about how you interact with our platform, including pages visited, features used, and time spent on the platform.",
        "Device Information: IP address, browser type, operating system, and device identifiers.",
        "Cookies and Tracking: We use cookies and similar technologies to enhance your experience and analyze platform usage."
      ]
    },
    {
      title: "How We Use Your Information",
      content: [
        "Program Matching: To analyze your profile and match you with suitable residency programs based on your qualifications and preferences.",
        "Service Improvement: To enhance our algorithms, develop new features, and improve the overall user experience.",
        "Communication: To send you important updates, notifications about your account, and respond to your inquiries.",
        "Analytics: To understand usage patterns and optimize our platform performance.",
        "Legal Compliance: To comply with applicable laws, regulations, and legal processes."
      ]
    },
    {
      title: "Information Sharing",
      content: [
        "We do not sell, trade, or rent your personal information to third parties.",
        "Service Providers: We may share information with trusted third-party service providers who assist us in operating our platform, processing payments, or providing analytics.",
        "Legal Requirements: We may disclose information when required by law, court order, or government request.",
        "Business Transfers: In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.",
        "Consent: We may share information with your explicit consent for specific purposes."
      ]
    },
    {
      title: "Data Security",
      content: [
        "Encryption: All sensitive data is encrypted both in transit and at rest using industry-standard encryption protocols.",
        "Access Controls: We implement strict access controls to ensure only authorized personnel can access your information.",
        "Regular Audits: We conduct regular security audits and assessments to identify and address potential vulnerabilities.",
        "Secure Infrastructure: Our platform is hosted on secure, compliant cloud infrastructure with robust security measures.",
        "Employee Training: All employees receive regular training on data protection and privacy best practices."
      ]
    },
    {
      title: "Your Rights and Choices",
      content: [
        "Access: You have the right to access and review the personal information we hold about you.",
        "Correction: You can update or correct your personal information through your account settings or by contacting us.",
        "Deletion: You may request deletion of your personal information, subject to certain legal and operational requirements.",
        "Portability: You can request a copy of your personal data in a structured, machine-readable format.",
        "Opt-out: You can opt out of non-essential communications and marketing emails at any time."
      ]
    },
    {
      title: "Data Retention",
      content: [
        "Account Data: We retain your account information for as long as your account is active or as needed to provide services.",
        "Educational Records: Academic and professional information may be retained for analytical purposes and service improvement.",
        "Usage Data: Non-personal usage data may be retained indefinitely for analytics and research purposes.",
        "Legal Requirements: We may retain certain information longer if required by law or for legitimate business purposes.",
        "Deletion Requests: Upon account deletion, we will remove or anonymize your personal information within 30 days, except where retention is required by law."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
<Link to="/" className="flex items-center">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-10 h-10 mr-4 object-contain"
/>
  <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
    Residency Companion
  </h1>
</Link>
            <div className="flex items-center space-x-1">
              <button className="text-gray-600 hover:text-gray-900 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-50">
                Sign In
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                Get Started
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced animation-delay-2000"></div>
        </div>
        
        <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-thin text-gray-900 mb-6 leading-tight">
              Privacy
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light">
                Policy
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto font-light leading-relaxed">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>

            <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-lg p-4 inline-block">
              <p className="text-sm text-gray-600">
                <strong>Last Updated:</strong> January 26, 2025
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="mb-16">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Commitment to Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                At Residency Companion, we understand the sensitive nature of your medical and academic information. 
                This Privacy Policy explains how we collect, use, share, and protect your personal information when you 
                use our platform. We are committed to maintaining the highest standards of data protection and transparency 
                in all our operations.
              </p>
            </div>
          </div>

          {/* Privacy Policy Sections */}
          <div className="space-y-12">
            {sections.map((section, index) => (
              <div 
                key={index} 
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h2 className="text-3xl font-semibold text-gray-900 mb-6 border-b border-gray-200 pb-3">
                  {section.title}
                </h2>
                <div className="space-y-4">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Additional Information */}
          <div className="mt-16 space-y-8">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">International Data Transfers</h3>
              <p className="text-gray-700 leading-relaxed">
                If you are accessing our services from outside the United States, please be aware that your information 
                may be transferred to, stored, and processed in the United States where our servers are located. We ensure 
                that such transfers comply with applicable data protection laws and implement appropriate safeguards to 
                protect your information.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Children's Privacy</h3>
              <p className="text-gray-700 leading-relaxed">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal 
                information from children under 18. If we become aware that we have collected personal information from a 
                child under 18, we will take steps to delete such information promptly.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Changes to This Policy</h3>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. 
                We will notify you of any material changes by posting the updated policy on our website and updating the 
                "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mt-16 bg-gradient-to-r from-blue-50 to-teal-50 border border-blue-200 rounded-lg p-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us About Privacy</h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please don't hesitate to contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <div><strong>Email:</strong> privacy@residencycompanion.com</div>
              <div><strong>Address:</strong> 123 Medical Drive, Suite 100, New York, NY 10001</div>
              <div><strong>Phone:</strong> +1 (555) 123-4567</div>
            </div>
            <div className="mt-6">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-300 hover:scale-105">
                Contact Privacy Team
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-6">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-12 h-12 mr-4 object-contain"
/>
                <div>
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                    Residency Companion
                  </h4>
                  <p className="text-sm text-gray-500 font-medium">Medical College Guidance Platform</p>
                </div>
              </div>
              <p className="text-gray-600 font-light">
                Your partner in medical education decisions.
              </p>
            </div>
<div>
  <h5 className="font-semibold text-gray-900 mb-4">Product</h5>
  <ul className="space-y-3 text-gray-600">
    <li><Link to="/features" className="hover:text-blue-600 transition-colors">Features</Link></li>
    <li><Link to="/how-it-works" className="hover:text-blue-600 transition-colors">How It Works</Link></li>
    <li><Link to="/api" className="hover:text-blue-600 transition-colors">API</Link></li>
  </ul>
</div>
<div>
  <h5 className="font-semibold text-gray-900 mb-4">Support</h5>
  <ul className="space-y-3 text-gray-600">
    <li><Link to="/help" className="hover:text-blue-600 transition-colors">Help Center</Link></li>
    <li><Link to="/contact" className="hover:text-blue-600 transition-colors">Contact Us</Link></li>
      <li><Link to="/refund-policy" className="hover:text-blue-600 transition-colors">Refund Policy</Link></li>
      <li><Link to="/cancellation-policy" className="hover:text-blue-600 transition-colors">Cancellation Policy</Link></li>
    <li><Link to="/privacy" className="text-blue-600 font-medium">Privacy</Link></li>
    <li><Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link></li>
  </ul>
</div>
<div>
  <h5 className="font-semibold text-gray-900 mb-4">Company</h5>
  <ul className="space-y-3 text-gray-600">
    <li><Link to="/about" className="hover:text-blue-600 transition-colors">About</Link></li>
  </ul>
</div>
          </div>
          <div className="border-t border-gray-200 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © 2025 Residency Companion. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes blob-enhanced {
          0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
          25% { transform: translate(20px, -30px) scale(1.05) rotate(5deg); }
          50% { transform: translate(-15px, 10px) scale(0.95) rotate(-3deg); }
          75% { transform: translate(25px, -20px) scale(1.08) rotate(7deg); }
          100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
        }
        .animate-blob-enhanced { animation: blob-enhanced 9s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default PrivacyPage;